import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { Save, X, Mail } from 'lucide-react';
import { cn } from '@caruso/ui';
import { emailTemplatesService, EmailTemplate } from '@/services/email-templates.service';
import { RichTextEditor } from '@/components/rich-text-editor';
import { AiContentGenerator } from '@/components/ai-content-generator';

export function EmailTemplateFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = id !== 'novo';

  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState<Partial<EmailTemplate>>({
    name: '',
    subject: '',
    body: '',
    category: '',
    variables: '',
    description: '',
    active: true,
  });

  useEffect(() => {
    if (isEdit && id) {
      loadTemplate(id);
    }
  }, [id, isEdit]);

  const loadTemplate = async (templateId: string) => {
    try {
      const data = await emailTemplatesService.getById(templateId);
      setTemplate(data);
    } catch (error) {
      toast({
        title: 'Erro ao carregar template',
        variant: 'destructive',
      });
      navigate('/app/email-templates');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template.name || !template.subject || !template.body) {
      toast({
        title: 'Preencha todos os campos obrigatórios',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      if (isEdit && id) {
        await emailTemplatesService.update(id, template);
        toast({ title: 'Template atualizado', variant: 'success' });
      } else {
        await emailTemplatesService.create(template as any);
        toast({ title: 'Template criado', variant: 'success' });
      }
      navigate('/app/email-templates');
    } catch (error) {
      toast({
        title: 'Erro ao salvar template',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">
            {isEdit ? 'Editar Template' : 'Novo Template'}
          </h1>
          <p className="text-brand-ivory/60 mt-1">
            Configure seu template de email
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/email-templates')}
          >
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button type="submit" variant="premium" disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      <Card className="bg-brand-ink/50 border-brand-gold/20 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
              Nome do Template *
            </label>
            <Input
              value={template.name}
              onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
              Categoria
            </label>
            <Input
              value={template.category || ''}
              onChange={(e) => setTemplate({ ...template, category: e.target.value })}
              placeholder="Ex: boas-vindas, notificação..."
              className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
            Assunto do Email *
          </label>
          <Input
            value={template.subject}
            onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-brand-ivory/80">
              Corpo do Email *
            </label>
            <AiContentGenerator
              onContentGenerated={(content) => setTemplate({ ...template, body: content })}
            />
          </div>
          <div className="mb-2 p-3 bg-brand-gold/10 border border-brand-gold/20 rounded-lg">
            <p className="text-xs text-brand-ivory/70">
              <strong className="text-brand-gold">Dica:</strong> Use variáveis no formato{' '}
              <code className="px-1.5 py-0.5 bg-brand-smoke/50 rounded text-brand-gold">
                &#123;&#123;variavel&#125;&#125;
              </code>{' '}
              para personalizar o email. Exemplo:{' '}
              <code className="px-1.5 py-0.5 bg-brand-smoke/50 rounded text-brand-gold">
                Olá &#123;&#123;name&#125;&#125;
              </code>
            </p>
          </div>
          <RichTextEditor
            content={template.body || ''}
            onChange={(html) => setTemplate({ ...template, body: html })}
            placeholder="Digite o conteúdo do email..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
            Variáveis (JSON array)
          </label>
          <Input
            value={template.variables || ''}
            onChange={(e) => setTemplate({ ...template, variables: e.target.value })}
            placeholder='["name", "email", "link"]'
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
          />
          <p className="text-xs text-brand-ivory/50 mt-1">
            Use &#123;&#123;variavel&#125;&#125; no template
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
            Descrição
          </label>
          <textarea
            value={template.description || ''}
            onChange={(e) => setTemplate({ ...template, description: e.target.value })}
            className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
            rows={3}
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="active"
            checked={template.active}
            onChange={(e) => setTemplate({ ...template, active: e.target.checked })}
            className="w-4 h-4"
          />
          <label htmlFor="active" className="text-sm text-brand-ivory/80">
            Template ativo
          </label>
        </div>
      </Card>
    </form>
  );
}
